import type { CookieInterface } from '@app/services/CookieManager'

import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'

import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'

import { profileUserSelector } from './profile'

const testFlightsSlice = createReduxSlice<{ data: CurrentTestFlight['flights']; hash: string }>('test_flights')

export const testFlightsResultSelector = createSelector([testFlightsSlice.selector, profileUserSelector], (slice, profile) =>
  createLoadable(c => {
    const hash = [profile?.id ?? ''].join(':')
    if (!slice || slice.hash !== hash) {
      return c.load(
        createPromise('Test flights must be fetched', hash, async dispatch => {
          const cookies = dispatch(getCookies())
          const data = await get(cookies)
          dispatch(testFlightsSlice.set({ data: data.flights, hash }))
        })
      )
    }
    return slice.data
  })
)

/**
 * Defines current test flights structure.
 * Test flight is a feature which is shown only to
 * particular group of users and is stored in cookie.
 * Think of it of a/b test of features
 *
 * Note, that in "flights" it is better to use keys that are valid variable names,
 * so, no dashes "-", or special symbols, only a-z letters, _ underscore and 0-9 numbers.
 * Doing otherwise might break typescript static check, and will fail silently
 */

export type CurrentTestFlight = {
  flights: {
    search_banner: boolean
  }
  version: 2
}

/** Defines union type of all versions of CurrentTestFlightStructure */
type TestFlight = CurrentTestFlight

const testFlightCookie = createCookie<null | TestFlight>(
  'kidsout__test-flight',
  input => {
    if (input === '') return null
    try {
      const flights = JSON.parse(input)
      if (typeof flights !== 'object') return null
      return flights
    } catch {
      return null
    }
  },
  input => JSON.stringify(input),
  () => ({ expires: moment().add(1, 'year'), sameSite: 'lax' })
)

const migrate = (data: any | null): CurrentTestFlight => {
  if (!data || !('flights' in data) || !('version' in data)) {
    data = { flights: {}, version: 0 }
  }
  while (data.version < 2) {
    switch (data.version as number) {
      case 0:
        data.flights.search_banner = Math.random() > 0.5
        break
      case 1:
        data.flights.search_new_window = Math.random() > 0.5
        break
    }
    ++data.version
  }
  return data
}

export const get = async (cookies: CookieInterface): Promise<CurrentTestFlight> => {
  let value = await testFlightCookie.get(cookies)
  if (!value || value.version !== 2) {
    value = migrate(typeof value === 'object' ? value : null)
    await testFlightCookie.set(cookies, value)
  }

  return value
}

export const set = async (cookies: CookieInterface, value: Partial<CurrentTestFlight['flights']>) => {
  const svalue = await get(cookies)

  const newValue = { ...svalue, flights: { ...svalue.flights, ...value } }

  await testFlightCookie.set(cookies, newValue)
}
